import { useState, useEffect } from 'react';
import { Save, RotateCcw, HardDrive, Clock, Archive, AlertCircle, Loader2 } from 'lucide-react';
import { Heading, Skeleton } from '@aaimplatform/ui';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';

const DATA_DEFAULTS = {
  auditLogRetention: 365,
  transactionRetention: 2555,
  backupFrequency: 'daily',
  backupRetention: 90,
  archiveEnabled: true,
  archiveAfter: 730,
  compressionEnabled: true,
  storageTotal: '2.4 TB',
  storageActive: '1.8 TB',
  storageArchived: '0.6 TB',
};

type DataSettings = typeof DATA_DEFAULTS;

// Client-side icon mapping for storage metrics
const STORAGE_ICONS = [
  { key: 'storageTotal' as const, label: 'Total Storage', icon: HardDrive },
  { key: 'storageActive' as const, label: 'Active Data', icon: Clock },
  { key: 'storageArchived' as const, label: 'Archived', icon: Archive },
];

/**
 * Data & Storage Configuration - Platform Admin
 *
 * Retention, backup, and archival policies
 */
export function DataConfigPage() {
  const { config, isLoading, error, save, isSaving, saveError } =
    usePlatformConfig<DataSettings>('data');
  const [hasChanges, setHasChanges] = useState(false);
  const [settings, setSettings] = useState<DataSettings>(DATA_DEFAULTS);

  useEffect(() => {
    if (config) {
      setSettings(config);
      setHasChanges(false);
    }
  }, [config]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    save(settings);
    setHasChanges(false);
  };

  const handleReset = () => {
    if (config) setSettings(config);
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-end gap-3 mb-6">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-sm" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-sm mb-6" />
        <Skeleton className="h-48 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
        <button
          onClick={handleReset}
          disabled={!hasChanges}
          className={`flex items-center gap-2 px-4 py-2.5 border border-recessed rounded-sm font-sans text-sm font-medium transition-colors ${
            hasChanges ? 'text-primary hover:bg-elevated' : 'text-tertiary cursor-not-allowed'
          }`}
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-sm font-sans text-sm font-medium transition-colors ${
            hasChanges && !isSaving
              ? 'bg-accent text-white hover:bg-accent-hover'
              : 'bg-recessed text-tertiary cursor-not-allowed'
          }`}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Error State */}
      {(error || saveError) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error ? 'Failed to load configuration' : 'Failed to save changes'}
          </div>
        </div>
      )}

      {/* Storage Overview */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {STORAGE_ICONS.map((metric) => (
          <div key={metric.key} className="bg-surface rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <metric.icon className="w-5 h-5 text-tertiary" />
              <div className="text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                {metric.label}
              </div>
            </div>
            <div className="text-2xl font-serif font-semibold tracking-headline-serif text-primary tabular-nums leading-none">
              {settings[metric.key]}
            </div>
          </div>
        ))}
      </div>

      {/* Data Retention */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-6">
          Data Retention
        </Heading>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Audit log retention (days)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                How long to keep audit logs
              </div>
            </div>
            <input
              type="number"
              value={settings.auditLogRetention}
              onChange={e => handleChange('auditLogRetention', parseInt(e.target.value))}
              className="w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Transaction history retention (days)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                How long to keep transaction records (7 years default)
              </div>
            </div>
            <input
              type="number"
              value={settings.transactionRetention}
              onChange={e => handleChange('transactionRetention', parseInt(e.target.value))}
              className="w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* Backup Settings */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-6">
          Backup Settings
        </Heading>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Backup frequency
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                How often to create backups
              </div>
            </div>
            <select
              value={settings.backupFrequency}
              onChange={e => handleChange('backupFrequency', e.target.value)}
              className="px-3 py-2 border border-recessed rounded-sm text-sm font-sans focus:outline-none focus:border-accent"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Backup retention (days)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                How long to keep backups
              </div>
            </div>
            <input
              type="number"
              value={settings.backupRetention}
              onChange={e => handleChange('backupRetention', parseInt(e.target.value))}
              className="w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* Archive Settings */}
      <div className="bg-surface rounded-sm shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <Heading level={3}>
            Archive Settings
          </Heading>
          <button
            onClick={() => handleChange('archiveEnabled', !settings.archiveEnabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              settings.archiveEnabled ? 'bg-accent' : 'bg-recessed'
            }`}
          >
            <div
              className={`w-5 h-5 bg-surface rounded-full shadow transition-transform ${
                settings.archiveEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Archive after (days)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Move data to cold storage after this period
              </div>
            </div>
            <input
              type="number"
              value={settings.archiveAfter}
              onChange={e => handleChange('archiveAfter', parseInt(e.target.value))}
              disabled={!settings.archiveEnabled}
              className={`w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent ${
                !settings.archiveEnabled ? 'bg-elevated text-tertiary' : ''
              }`}
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Enable compression
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Compress archived data to save storage
              </div>
            </div>
            <button
              onClick={() => handleChange('compressionEnabled', !settings.compressionEnabled)}
              disabled={!settings.archiveEnabled}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.compressionEnabled && settings.archiveEnabled ? 'bg-accent' : 'bg-recessed'
              }`}
            >
              <div
                className={`w-5 h-5 bg-surface rounded-full shadow transition-transform ${
                  settings.compressionEnabled && settings.archiveEnabled ? 'translate-x-6' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
